import React, { useState } from "react";
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";

import { type AppUserSession } from "@/schemas/auth/session";
import { UserImageType } from "@/schemas/user/image";
import universalUploadImage from "@/utils/image/universalUploadImage";

type Props = {
  session: AppUserSession;
  onUploadSuccess: () => void;
};

function UploadPublicPhotoPrompt({
  session,
  onUploadSuccess,
}: Props): React.JSX.Element {
  const [isUploading, setIsUploading] = useState(false);

  const handleImageUpload = async (): Promise<void> => {
    await universalUploadImage({
      session,
      imageType: UserImageType.PUBLIC_IMAGE,
      onUploadStart: () => setIsUploading(true),
      onUploadSuccess: () => {
        onUploadSuccess();
      },
      onUploadEnd: () => setIsUploading(false),
    });
  };

  return isUploading ? (
    <ActivityIndicator
      size={"large"}
      style={styles.activityIndicatorContainer}
    />
  ) : (
    <View style={styles.contentContainer}>
      <TouchableOpacity
        onPress={() => handleImageUpload()}
        disabled={isUploading}
      ></TouchableOpacity>
      <Text style={styles.title}>🌈 Be Seen, Be Real 🌈</Text>
      <Text style={styles.body}>
        Add a public photo featuring you, then explore other users’ photos—no
        pets or landscapes alone, please!
      </Text>
      <LinearGradient
        colors={[
          "#FFB3B3",
          "#FFD6A5",
          "#FFFACD",
          "#C7F9CC",
          "#A0C4FF",
          "#BDB2FF",
          "#FFC6FF",
        ]}
        style={styles.rainbowLine}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
      />
      <TouchableOpacity onPress={() => handleImageUpload()}>
        <View style={styles.button}>
          <Feather name={"upload"} size={16} color={"rgb(11,71,135)"} />
          <Text style={styles.buttonText}>Upload a Photo With You in It</Text>
        </View>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  activityIndicatorContainer: {
    marginTop: 15,
  },
  contentContainer: {
    backgroundColor: "transparent",
    paddingTop: 20,
    paddingHorizontal: 30,
    // justifyContent: "center",
    alignItems: "center",
  },
  title: {
    fontSize: 24,
    color: "#555",
    fontWeight: 500,
    marginBottom: 10,
    textShadowColor: "rgba(0, 0, 0, 0.25)",
    textShadowOffset: { width: -1, height: 1 },
    textShadowRadius: 10,
  },
  body: {
    fontSize: 16,
    color: "#555",
    fontWeight: 500,
    textShadowColor: "rgba(0, 0, 0, 0.25)",
    textShadowOffset: { width: -1, height: 1 },
    textShadowRadius: 20,
  },
  button: {
    borderRadius: 5,
    paddingHorizontal: 40,
    paddingVertical: 10,
    flexDirection: "row",
    textAlign: "center",
    backgroundColor: "rgb(239,187,40)",
  },
  buttonText: {
    marginLeft: 10,
    fontWeight: "bold",
    color: "rgb(11,71,135)",
  },
  rainbowLine: {
    height: 2,
    borderRadius: 2,
    marginVertical: 20,
    width: "100%",
  },
});

export default UploadPublicPhotoPrompt;
